import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getHomeRouteByRole } from "../../utils/rutas";
import { API_URL } from "../../config";
import { getAccionSistemaTipoLabel } from "../../utils/accionSistemaLabels";
import {
  getAccionTipoFilterOptions,
  getRolPerfilFilterOptions,
} from "../../utils/accionesFiltroCatalog";
import { sortAccionesByFechaDesc, applyAccionesFiltros } from "../../utils/accionesListadoUtils";
import { unwrapAccionesListPayload } from "../../utils/unwrapAccionesListPayload";
import {
  buildAccionAffectedProfileLine,
  buildAccionCardHeadline,
  buildAccionDetailBody,
} from "../../utils/activityLogAccionCard";
import AccionesFiltroPanel from "../../components/AccionesFiltroPanel";
import ActivityRecordsListScroller from "../../components/ActivityRecordsListScroller";
import ResponsiveActionSidebar from "../../components/ResponsiveActionSidebar";
import es from "../../data/locales/es.json";
import "./Registro.css";

const activityUi = es.activityLog;

const DEFAULT_ACT_FILTROS = {
  nombre: "",
  accionTipo: "",
  fechaDesde: "",
  fechaHasta: "",
  rol: "",
};

const Registro = () => {
  const navigate = useNavigate();
  const [rol, setRol] = useState("");
  const [acciones, setAcciones] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [filtrosDraft, setFiltrosDraft] = useState(DEFAULT_ACT_FILTROS);
  const [filtrosApplied, setFiltrosApplied] = useState(DEFAULT_ACT_FILTROS);

  const tipoOptions = useMemo(
    () => getAccionTipoFilterOptions(activityUi.filterTodosTipos),
    [],
  );
  const rolOptions = useMemo(
    () => getRolPerfilFilterOptions(activityUi.filterTodosRoles, activityUi.roles),
    [],
  );

  const accionesVisibles = useMemo(
    () => applyAccionesFiltros(sortAccionesByFechaDesc(acciones), filtrosApplied),
    [acciones, filtrosApplied],
  );

  const handleApplyFiltros = useCallback(() => {
    setFiltrosApplied({ ...filtrosDraft });
  }, [filtrosDraft]);

  const handleClearFiltros = useCallback(() => {
    setFiltrosDraft(DEFAULT_ACT_FILTROS);
    setFiltrosApplied(DEFAULT_ACT_FILTROS);
  }, []);

  const filterPanelLabels = {
    nombre: activityUi.listFilterNombre,
    tipo: activityUi.filterTipo,
    rol: activityUi.listFilterRol,
    desde: activityUi.filterDesde,
    hasta: activityUi.filterHasta,
    apply: activityUi.filterApply,
    clear: activityUi.clear,
  };

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) setRol(data.usuario.rol);
      })
      .catch((err) => console.error("Error obteniendo rol:", err));
  }, []);

  const handleVolver = () => {
    navigate(getHomeRouteByRole(rol), { replace: true });
  };

  useEffect(() => {
    obtenerAcciones();
  }, []);

  const obtenerAcciones = async () => {
    try {
      const response = await fetch(`${API_URL}/acciones-usuario`, {
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        setAcciones(unwrapAccionesListPayload(data));
      } else {
        setMensaje(data.error || "Error al obtener acciones");
      }
    } catch (error) {
      setMensaje("Error de conexión al obtener acciones");
    }
  };

  const calcularDiasRestantes = (fechaExpiracion) => {
    if (!fechaExpiracion) return null;
    const ahora = new Date();
    const expiracion = new Date(fechaExpiracion);
    const diff = expiracion - ahora;
    if (diff <= 0) return "Cuenta finalizada";
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) + " días restantes";
  };

  return (
    <div className="reg-container">
      <div className="reg-main-container">
        <div className="reg-content">
          <h1>Registro de Actividades</h1>

          {acciones.length === 0 ? (
            <p>No hay actividades registradas.</p>
          ) : (
            <>
              <AccionesFiltroPanel
                draftFiltros={filtrosDraft}
                onDraftChange={setFiltrosDraft}
                onApply={handleApplyFiltros}
                onClear={handleClearFiltros}
                tipoOptions={tipoOptions}
                rolOptions={rolOptions}
                labels={filterPanelLabels}
              />
              {accionesVisibles.length === 0 ? (
                <p>{activityUi.empty}</p>
              ) : (
                <ActivityRecordsListScroller>
                  <div className="reg-acciones-lista">
                    {accionesVisibles.map((accion) => {
                      const affectedLine = buildAccionAffectedProfileLine(accion, activityUi);
                      return (
                        <div key={accion._id} className="reg-accion-card">
                          <h4>
                            {buildAccionCardHeadline(accion, getAccionSistemaTipoLabel, activityUi)}
                          </h4>
                          {affectedLine ? (
                            <p className="reg-accion-affected">{affectedLine}</p>
                          ) : null}
                          <p>
                            <strong>{activityUi.fecha}:</strong>{" "}
                            {new Date(accion.fecha || accion.createdAt).toLocaleString()}
                          </p>
                          {accion.tipo === "eliminacion" && (
                            <p>
                              <strong>Cuenta atrás:</strong>{" "}
                              {calcularDiasRestantes(accion.fechaExpiracion)}
                            </p>
                          )}
                          <details className="reg-accion-observaciones">
                            <summary>{activityUi.detailsSummary}</summary>
                            <p className="reg-accion-observaciones-body">
                              {buildAccionDetailBody(accion, activityUi)}
                            </p>
                          </details>
                        </div>
                      );
                    })}
                  </div>
                </ActivityRecordsListScroller>
              )}
            </>
          )}
          {mensaje && <p className="reg-message">{mensaje}</p>}
        </div>

        <ResponsiveActionSidebar
          id="registro-sidebar"
          className="reg-sidebar"
          openClassName="reg-sidebar--open"
          ariaLabel="Acciones del registro"
        >
          <h4>Acciones</h4>
          <ul className="reg-sidebar-links">
            <li>
              <button className="reg-sidebar-button staff-sidebar-exit-button" onClick={handleVolver}>
                Salir
              </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>
    </div>
  );
};

export default Registro;
